const BASE_URL = import.meta.env.VITE_API_BASE_URL;

async function request(path, { method = "GET", body, idToken } = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(idToken ? { Authorization: idToken } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${method} ${path} failed: ${res.status} ${text}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

// The CSV export is the one endpoint that does not return JSON, so it cannot
// go through `request` above. The filename is rebuilt here rather than read
// from Content-Disposition: a cross-origin response only exposes headers named
// in Access-Control-Expose-Headers, and depending on that config for something
// as visible as a downloaded file's name is a fragile trade for no benefit.
async function requestText(path, { idToken } = {}) {
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { ...(idToken ? { Authorization: idToken } : {}) },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`GET ${path} failed: ${res.status} ${text}`);
  }
  return res.text();
}

export const api = {
  getAccounts: (idToken) => request("/accounts", { idToken }),
  createAccount: (idToken, payload) => request("/accounts", { method: "POST", body: payload, idToken }),
  deleteAccount: (idToken, accountId) => request(`/accounts/${accountId}`, { method: "DELETE", idToken }),
  // Edits in place — currently monthly_budget and account_name. Anything that
  // changes how an account is READ (org role, parent, credentials) still goes
  // through a full createAccount re-registration, deliberately.
  // Pass monthly_budget: null to clear a budget rather than set it to zero.
  updateAccount: (idToken, accountId, patch) =>
    request(`/accounts/${accountId}`, { method: "PATCH", body: patch, idToken }),
  getCostSummary: (idToken, period) => request(`/costs/summary?period=${period}`, { idToken }),
  // `compare: "previous"` adds previous_total to each account row (that
  // account's total for the month before `period`), computed server-side from
  // the same snapshot table — omit it and the response is byte-identical to
  // what it was before the option existed.
  getCostByAccount: (idToken, period, compare) =>
    request(`/costs/by-account?period=${period}${compare ? `&compare=${compare}` : ""}`, { idToken }),
  // Actual per-day cost for a calendar month plus the whole previous month,
  // for the Spend page's daily chart. Omit accountId for the consolidated
  // series across every registered account. This is the only endpoint backed
  // by a DAILY-granularity Cost Explorer query — everything else is monthly.
  // `tagFilter` ({ key, values: [...] }) restricts the series and the AWS
  // forecast to resources carrying that tag — the Spend page's tag filter.
  getCostDaily: (idToken, period, accountId, force, tagFilter) => {
    const qp = new URLSearchParams({ period });
    if (accountId) qp.set("account_id", accountId);
    if (force) qp.set("force", "true");
    if (tagFilter?.key && tagFilter?.values?.length) {
      qp.set("tag_key", tagFilter.key);
      qp.set("tag_values", tagFilter.values.join(","));
    }
    return request(`/costs/daily?${qp.toString()}`, { idToken });
  },
  // Service x account pairs ranked by absolute change vs the previous month.
  // One request for the whole "What moved" list — the alternative is a
  // by-service call per account per month, which does not scale past a
  // handful of accounts.
  getCostMovers: (idToken, period, limit) => {
    const qp = new URLSearchParams({ period, compare: "previous" });
    if (limit) qp.set("limit", String(limit));
    return request(`/costs/movers?${qp.toString()}`, { idToken });
  },
  // `range` selects the query mode: pass either { period: "YYYY-MM" } or
  // { startDate: "YYYY-MM-DD", endDate: "YYYY-MM-DD" }. `force` bypasses the
  // backend's cache (only meaningful for a date range — calendar-month
  // lookups always read CostDataTable's pre-aggregated snapshot directly).
  // `tagFilter`, when given as { key, values: [...] }, restricts results to
  // resources carrying that tag (see get_cost_tags below for discovering
  // available keys/values).
  // `compare: "previous"` (calendar-month mode only) adds previous_amount to
  // every service row. The backend rejects it alongside start_date/end_date
  // rather than guessing what "previous" means for an arbitrary window.
  getCostByService: (idToken, accountId, range, force, tagFilter, compare) => {
    const qp = new URLSearchParams({ account_id: accountId });
    if (range?.startDate && range?.endDate) {
      qp.set("start_date", range.startDate);
      qp.set("end_date", range.endDate);
    } else if (range?.period) {
      qp.set("period", range.period);
    }
    if (force) qp.set("force", "true");
    if (tagFilter?.key && tagFilter?.values?.length) {
      qp.set("tag_key", tagFilter.key);
      qp.set("tag_values", tagFilter.values.join(","));
    }
    if (compare) qp.set("compare", compare);
    return request(`/costs/by-service?${qp.toString()}`, { idToken });
  },
  getCostUsageTypes: (idToken, accountId, service, range, force, tagFilter, compare) => {
    const qp = new URLSearchParams({ account_id: accountId, service });
    if (range?.startDate && range?.endDate) {
      qp.set("start_date", range.startDate);
      qp.set("end_date", range.endDate);
    } else if (range?.period) {
      qp.set("period", range.period);
    }
    if (force) qp.set("force", "true");
    if (tagFilter?.key && tagFilter?.values?.length) {
      qp.set("tag_key", tagFilter.key);
      qp.set("tag_values", tagFilter.values.join(","));
    }
    if (compare) qp.set("compare", compare);
    return request(`/costs/by-service/usage-types?${qp.toString()}`, { idToken });
  },
  // Tag-filter discovery: omit tagKey to list available tag KEYS for this
  // account/range; pass a tagKey to list available VALUES for that key.
  // Tag keys (omit tagKey) or values (give tagKey) across EVERY registered
  // account for a date range — the Spend page's tag filter. The keys response
  // also lists `inactive_keys` (on resources but not activated for billing)
  // and `active_without_data`.
  getCostTagsAll: (idToken, startDate, endDate, tagKey) => {
    const qp = new URLSearchParams({ start_date: startDate, end_date: endDate });
    if (tagKey) qp.set("tag_key", tagKey);
    return request(`/costs/tags?${qp.toString()}`, { idToken });
  },
  // One month of cost carrying a tag value, by account and service, with the
  // same days of last month alongside (whole month once it's closed). Live
  // Cost Explorer behind a 6h/30d cache.
  getCostTagged: (idToken, period, tagFilter, force) => {
    const qp = new URLSearchParams({ period, tag_key: tagFilter.key, tag_values: tagFilter.values.join(",") });
    if (force) qp.set("force", "true");
    return request(`/costs/tagged?${qp.toString()}`, { idToken });
  },
  getCostTags: (idToken, accountId, range, tagKey) => {
    const qp = new URLSearchParams({ account_id: accountId, start_date: range.startDate, end_date: range.endDate });
    if (tagKey) qp.set("tag_key", tagKey);
    return request(`/costs/tags?${qp.toString()}`, { idToken });
  },
  // Cost-optimization recommendations merged from Cost Optimization Hub, Cost
  // Explorer rightsizing, Savings Plans / RI purchase recommendations, and a
  // spend-trend heuristic computed from the snapshot table. Cached 12h
  // server-side; `force` re-queries AWS (those are billed calls).
  getSavings: (idToken, period, force) => {
    const qp = new URLSearchParams({ period });
    if (force) qp.set("force", "true");
    return request(`/savings?${qp.toString()}`, { idToken });
  },
  // Account x service detail for one month as CSV. Scoped to a payer account
  // (an AWS invoice covers exactly one payer group); omit payerAccountId for
  // every registered account in one file. Served from the snapshot table, so
  // this costs nothing and is instant.
  exportCosts: (idToken, period, payerAccountId) => {
    const qp = new URLSearchParams({ period });
    if (payerAccountId) qp.set("payer_account_id", payerAccountId);
    return requestText(`/costs/export?${qp.toString()}`, { idToken });
  },
  getCostTrend: (idToken, months, accountId) =>
    request(`/costs/trend?months=${months}${accountId ? `&account_id=${accountId}` : ""}`, { idToken }),
  refresh: (idToken, months) => request(`/refresh${months ? `?months=${months}` : ""}`, { method: "POST", idToken }),
  listInvoices: (idToken, accountId, startDate, endDate, force) => {
    const qp = new URLSearchParams({ account_id: accountId, start_date: startDate, end_date: endDate });
    if (force) qp.set("force", "true");
    return request(`/invoices?${qp.toString()}`, { idToken });
  },
  // Returns a fresh, short-lived (~15 min) presigned download URL — call
  // this right before opening it, never cache the result client-side.
  getInvoicePdf: (idToken, accountId, invoiceId) =>
    request(`/invoices/${encodeURIComponent(invoiceId)}/pdf?account_id=${accountId}`, { idToken }),
};
